import React, {useState} from "react";
import {Button} from "primereact/button";
import {formatCurrency, getHostName} from "../../utils/Utils";
import {computePriceStats} from "./productDetailStats";
import {isOwnRowCampaignInactive} from "./campaignInactiveSeller";
import {pricePosition, profitLoss} from "./signals/productSignals";
import PriceRangeBand from "./PriceRangeBand";
import RuleDetails from "../Pricing/RuleDetails";
import MarketplaceHero from "./marketplace/MarketplaceHero";
import {readMarketplaceOperator} from "../Campaign/marketplaceOperatorSettings";

// A compact metric tile: colored icon badge + value + label.
const HeroMetric = ({icon, color, bg, value, label}) => (
    <div className="pux-metric">
        <span className="pux-metric-icon" style={{background: bg, color}}><i className={"pi " + icon}/></span>
        <div>
            <div className="pux-metric-val" style={{color}}>{value}</div>
            <div className="pux-metric-lab">{label}</div>
        </div>
    </div>
);

// Price-position taxonomy shared with the product list status chip (signals/StatusCell.js)
// so the verdict label matches across the list and detail screens.
// Red -> green gradient by price position (most expensive -> cheapest).
const POSITION_LABEL = {
    highest: { key: 'product.myprice_highest',    color: 'red' },
    high:    { key: 'product.myprice_avg_higher', color: 'orange' },
    average: { key: 'product.myprice_average',    color: 'yellow' },
    low:     { key: 'product.myprice_avg_lower',  color: 'cyan' },
    same:    { key: 'product.myprice_same',       color: 'blue' },
    lowest:  { key: 'product.myprice_lowest',     color: 'green' },
};
const verdictColorClass = (color) => 'pux-hero-verdict-' + color;

// Latest in-stock price of a competitor (stock >= 1, a listed price), or undefined.
const lastInStockPrice = (competitor) => {
    if (!competitor || !competitor.prices || competitor.prices.length === 0) return undefined;
    const last = competitor.prices[competitor.prices.length - 1];
    return (last && last.stock >= 1 && last.value > 0) ? last.value : undefined;
};

// props: profile, t, selectedProduct, myProduct, competitors, onPrimaryAction(actionKey)
const DecisionHero = (props) => {
    const { t, profile, selectedProduct, selectedCampaign, appliedTemplateName, myProduct, competitors, onPrimaryAction } = props;
    // Hover state for the compact dynamic-pricing chip -> full rule card popover.
    // Declared before any early return to keep hook order stable.
    const [dpHover, setDpHover] = useState(false);
    const stats = computePriceStats(myProduct, competitors);

    // --- Marketplace operator: replaces the competitive hero entirely (no price
    // band, no repricing actions); a product without a summary shows no hero.
    if (profile.heroType === 'marketplace') {
        return <MarketplaceHero t={t} summary={selectedProduct.marketplace_summary}
                                config={readMarketplaceOperator(selectedCampaign?.settings)}
                                settings={selectedCampaign?.settings}
                                campaignId={selectedCampaign?.id}
                                product={selectedProduct}
                                currency={selectedProduct.marketplace_summary?.mp_currency || stats.currency || selectedProduct.currency}/>;
    }

    // --- Brand: conditional MAP/MRP/unauthorized verdict
    if (profile.heroType === 'brand') {
        const comps = competitors || [];
        // MAP/MRP values come from the is_map/is_mrp reference rows (same source as the table).
        const refVal = (row) => { const lp = row && row.prices && row.prices.length ? row.prices[row.prices.length - 1] : null; return lp && lp.value != null ? lp.value : null; };
        const mapVal = refVal(comps.find(c => c.is_map));
        const mrpVal = refVal(comps.find(c => c.is_mrp));

        // Real sellers only: count in-stock, MAP violators (price < MAP), MRP violators (price > MRP).
        const realSellers = comps.filter(c => !c.is_map && !c.is_mrp && !c.is_mine);
        let mapV = 0, mrpV = 0, inStock = 0;
        realSellers.forEach(c => {
            const price = lastInStockPrice(c);
            if (price === undefined) return;
            inStock++;
            if (mapVal && price < mapVal) mapV++;
            if (mrpVal && price > mrpVal) mrpV++;
        });

        // Unauthorized (whitelist): only meaningful when the campaign has authorization data.
        const authOf = (c) => {
            if (!c.product_url) return null;
            const host = getHostName(c.product_url);
            const site = (selectedCampaign?.competitors || []).find(comp => getHostName(comp.url).includes(host));
            if (!site) return false;
            if (site.is_authorized) return true;
            return !!(site.sellers || []).some(s => s.seller_name === c.name && s.is_authorized);
        };
        const hasAuthData = !!selectedCampaign?.competitors?.some(c => c.is_authorized || c.sellers?.some(s => s.is_authorized));
        const unauthorizedCount = hasAuthData ? realSellers.filter(c => authOf(c) === false).length : 0;
        const clean = mapV === 0 && mrpV === 0 && unauthorizedCount === 0;

        const metric = (icon, color, bg, value, label) => <HeroMetric icon={icon} color={color} bg={bg} value={value} label={label}/>;

        return <div className="pux-hero">
            <div className="pux-hero-left">
                {clean
                    ? <span className={"pux-hero-verdict-label " + verdictColorClass('green')}>{t('product_detail.all_compliant', 'All sellers compliant')}</span>
                    : <span className={"pux-hero-verdict-label " + verdictColorClass('red')}>{t('product_detail.has_violation', 'Has violation')}</span>}
                <span className="pux-hero-signal-lab">{t('product_detail.compliance', 'Compliance')}</span>
            </div>
            <div style={{flex:'1 1 320px', display:'flex', flexWrap:'wrap', gap:'10px', alignItems:'center'}}>
                {metric('pi-ban', mapV > 0 ? '#dc2626' : '#9ca3af', mapV > 0 ? '#fee2e2' : '#f3f4f6', mapV, t('product.filter_violation_map_label'))}
                {metric('pi-ban', mrpV > 0 ? '#dc2626' : '#9ca3af', mrpV > 0 ? '#fee2e2' : '#f3f4f6', mrpV, t('product.filter_violation_mrp_label'))}
                {hasAuthData && metric('pi-user-minus', unauthorizedCount > 0 ? '#7c3aed' : '#9ca3af', unauthorizedCount > 0 ? '#ede9fe' : '#f3f4f6', unauthorizedCount, t('product.filter_unauthorized'))}
                {metric('pi-arrow-down', '#0369a1', '#e0f2fe', formatCurrency(mapVal, stats.currency), t('product_detail.map_value', 'MAP value'))}
                {mrpVal != null && metric('pi-arrow-up', '#0369a1', '#e0f2fe', formatCurrency(mrpVal, stats.currency), t('product_detail.mrp_value', 'MRP value'))}
                {metric('pi-shopping-bag', '#2563eb', '#dbeafe', inStock, t('product_detail.in_stock_sellers', 'In-stock sellers'))}
            </div>
            <div className="pux-hero-right">
                <Button label={t('product_detail.configure_violation', 'Configure price violation')} className="p-button-sm"
                        icon="pi pi-cog" onClick={() => onPrimaryAction('compliance')}/>
            </div>
        </div>;
    }

    // --- Market search: no own listing. Aggregate over ALL real in-stock sellers
    // (the competitors() split treats the first seller as "mine", so compute from the
    // full product list here instead, otherwise the only seller is dropped).
    if (profile.heroType === 'marketsearch') {
        const sellers = (selectedProduct.competitors || []).filter(c => !c.is_parent_url && !c.is_map && !c.is_mrp);
        const prices = sellers.map(lastInStockPrice).filter(p => p !== undefined);
        const minP = prices.length ? Math.min(...prices) : undefined;
        const maxP = prices.length ? Math.max(...prices) : undefined;
        const avgP = prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : undefined;
        let currency;
        for (const c of sellers) { const lp = c.prices && c.prices.length ? c.prices[c.prices.length - 1] : null; if (lp && lp.currency) { currency = lp.currency; break; } }
        return <div className="pux-hero">
            <div className="pux-hero-left">
                <span className="pux-hero-verdict-label pux-hero-verdict-gray"><i className="pi pi-shopping-bag" style={{marginRight:'4px'}}/>{t('product_detail.market', 'Market')}</span>
                <span className="pux-hero-signal-lab">{prices.length} {t('product_detail.in_stock_sellers', 'In-stock sellers')}</span>
            </div>
            <div style={{flex:'1 1 320px', display:'flex', flexWrap:'wrap', gap:'10px', alignItems:'center'}}>
                <HeroMetric icon="pi-arrow-down" color="#16a34a" bg="#dcfce7" value={formatCurrency(minP, currency)} label={t('product_detail.kpi_min_of_comp')}/>
                <HeroMetric icon="pi-minus" color="#475569" bg="#f1f5f9" value={formatCurrency(avgP, currency)} label={t('product_detail.kpi_avg_of_comp')}/>
                <HeroMetric icon="pi-arrow-up" color="#d97706" bg="#fef3c7" value={formatCurrency(maxP, currency)} label={t('product_detail.kpi_max_of_comp')}/>
            </div>
        </div>;
    }

    // --- Competitive / Dropshipping: position (or profit/loss) verdict + suggestion
    const isDrop = profile.heroType === 'dropshipping';
    const cost = selectedProduct.drop_shipping_cost;
    const pl = isDrop ? profitLoss({price: stats.myPrice, drop_shipping_cost: cost}) : null;

    // Same signal + labels as the product list (signals/pricePosition + myprice_*) for
    // cross-screen consistency; min/max use the same in-stock definition as the backend.
    const pos = pricePosition({price: stats.myPrice, competitorsMinPrice: stats.minPrice, competitorsMaxPrice: stats.maxPrice});
    const posInfo = pos ? POSITION_LABEL[pos.level] : null;
    const posIcon = pos ? ({lowest:'pi-angle-double-down', same:'pi-angle-double-down', low:'pi-angle-down', average:'pi-minus', high:'pi-angle-up', highest:'pi-angle-double-up'}[pos.level] || 'pi-minus') : null;

    // My price rank among in-stock sellers (me included): "rank / total in-stock sellers".
    let rank = null;
    if (stats.myPrice != null && stats.myStock) {
        const cheaper = (competitors || []).map(lastInStockPrice).filter(p => p !== undefined && p < stats.myPrice).length;
        rank = { pos: cheaper + 1, total: stats.inStockCount + 1 };
    }

    const sugg = selectedProduct.suggested_price;
    const target = selectedProduct.target_price;
    // The floor the repricer will not cross. Product-level only -- price protection
    // bounds are per-product absolutes and are never inherited from the campaign, so
    // read the same field the Dynamic Pricing tab's protection card reads, with the
    // same "zero / null / undefined means not set" rule.
    const minLimit = selectedProduct.pricing_strategy && selectedProduct.pricing_strategy.min_value;
    // Suggestion is meaningful when the price to apply differs from my current price.
    const hasSuggestion = sugg != null && stats.myPrice != null && Math.abs(sugg - stats.myPrice) > 0.009;
    const up = hasSuggestion && sugg > stats.myPrice;

    // Dynamic-pricing: a COMPACT source chip (just the rule/source name to save
    // space) that opens the Dynamic Pricing tab on click and reveals the full
    // rule card on hover. pricing_strategy stores a SIGNED reduction_value with no
    // reduction_direction, so derive the direction from the sign for display.
    const dps = selectedProduct.pricing_strategy;
    const ruleFromStrategy = (ps, name) => ps && ps.reduction_type ? {
        name,
        reduction_type: ps.reduction_type,
        reduction_value: ps.reduction_value,
        reduction_direction: (ps.reduction_value || 0) < 0 ? -1 : 1,
        protection_strategy: ps.protection_strategy,
        is_active: ps.is_active,
        is_auto_update: ps.is_auto_update,
        no_competitor_strategy: ps.no_competitor_strategy,
    } : null;

    // chip* = the always-visible compact label; dpRule / dpCard* = the hover card.
    let chipLabel = null, chipClass = null, chipIcon = null;
    let dpRule = null, dpCardTitle = null, dpCardNote = null, dpCardIcon = null;
    if (selectedProduct.pricing_template_id) {
        chipLabel = appliedTemplateName || t('reprice.from_template'); chipClass = 'pux-chip-violet'; chipIcon = 'pi-bolt';
        dpRule = ruleFromStrategy(dps, chipLabel);
        if (!dpRule) { dpCardTitle = chipLabel; dpCardIcon = chipIcon; }
    } else if (dps && dps.reduction_type === 'campaign') {
        chipLabel = t('reprice.campaign'); chipClass = 'pux-chip-blue'; chipIcon = 'pi-megaphone';
        dpRule = ruleFromStrategy(selectedProduct.campaign && selectedProduct.campaign.pricing_strategy, chipLabel);
        if (!dpRule) { dpCardTitle = chipLabel; dpCardNote = t('reprice.follows_campaign'); dpCardIcon = chipIcon; }
    } else if (dps) {
        chipLabel = t('reprice.product'); chipClass = 'pux-chip-green'; chipIcon = 'pi-box';
        dpRule = ruleFromStrategy(dps, chipLabel);
    } else if (selectedProduct.campaign && selectedProduct.campaign.pricing_strategy && selectedProduct.campaign.pricing_strategy.is_active) {
        // No product-level strategy but the product inherits its OWN campaign's active
        // one. Use the product's campaign, NOT the header-selected one.
        chipLabel = t('reprice.campaign'); chipClass = 'pux-chip-blue'; chipIcon = 'pi-megaphone';
        dpRule = ruleFromStrategy(selectedProduct.campaign.pricing_strategy, chipLabel);
        if (!dpRule) { dpCardTitle = chipLabel; dpCardNote = t('reprice.follows_campaign'); dpCardIcon = chipIcon; }
    }
    const hasCard = !!(dpRule || dpCardTitle);

    return <div className="pux-hero">
        <div className="pux-hero-left">
            {isDrop && pl
                ? <span className={"pux-hero-verdict-label " + verdictColorClass(pl.loss ? 'red' : 'green')}>
                    <i className={"pi " + (pl.loss ? 'pi-arrow-down' : 'pi-arrow-up')} style={{marginRight:'4px'}}/>
                    {t(pl.loss ? 'product.loss' : 'product.profit')} {formatCurrency(Math.abs(pl.margin), stats.currency)}
                  </span>
                : posInfo
                    ? <span className={"pux-hero-verdict-label " + verdictColorClass(posInfo.color)}>{posIcon && <i className={"pi " + posIcon} style={{marginRight:'4px'}}/>}{t(posInfo.key)}</span>
                    : <span className="pux-hero-verdict-label pux-hero-verdict-gray">-</span>}
            <div style={{display:'flex', gap:'6px', flexWrap:'wrap', marginTop:'4px'}}>
                {isOwnRowCampaignInactive(selectedCampaign, myProduct)
                    ? <span className="pux-chip pux-chip-gray" title={t('product_detail.campaign_based_deactivated_seller')}>
                        <i className="pi pi-info-circle"/> {t('product_detail.filter_campaign_inactive')}
                      </span>
                    : <span className={"pux-chip " + (stats.myStock ? 'pux-chip-green' : 'pux-chip-gray')}>
                        <i className={"pi " + (stats.myStock ? 'pi-check-circle' : 'pi-times-circle')}/> {stats.myStock ? t('product_detail.in_stock', 'In stock') : t('product_detail.out_of_stock', 'Out of stock')}
                      </span>}
                {rank && <span className="pux-chip pux-chip-blue">
                    <i className="pi pi-sort-amount-down"/> {t('product_detail.my_position', 'My position')}: {rank.pos} / {rank.total}
                </span>}
                {isDrop && cost != null && <span className="pux-chip pux-chip-gray">
                    <i className="pi pi-wallet"/> {t('product_detail.cost', 'Cost')}: {formatCurrency(cost, stats.currency)}
                </span>}
            </div>
        </div>
        <div className="pux-hero-mid">
            {stats.minPrice === undefined && stats.myPrice != null && (
                <div className="pux-own-price-hint">
                    {t('product_detail.you', 'You')}: {formatCurrency(stats.myPrice, stats.currency)}
                </div>
            )}
            <PriceRangeBand t={t} type={profile.rangeVizType} stats={stats}/>
        </div>
        <div className="pux-hero-right">
            {chipLabel && <div style={{position:'relative', display:'flex', justifyContent:'flex-end'}}
                 onMouseEnter={() => setDpHover(true)} onMouseLeave={() => setDpHover(false)}>
                <span className={"pux-chip " + chipClass} style={{cursor:'pointer'}}
                      onClick={() => onPrimaryAction('pricing')} title={t('product_detail.repricing')}>
                    <i className={"pi " + chipIcon}/> {chipLabel}
                </span>
                {hasCard && dpHover && <div onClick={() => onPrimaryAction('pricing')}
                    style={{position:'absolute', top:'100%', right:0, zIndex:1000, marginTop:6, cursor:'pointer',
                        width:300, border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 12px',
                        background:'#fff', boxShadow:'0 6px 24px rgba(0,0,0,0.12)'}}>
                    {dpRule
                        ? <RuleDetails rule={dpRule} t={t} showHeader/>
                        : <div style={{display:'flex', flexDirection:'column', gap:4}}>
                            <span style={{fontWeight:700, display:'flex', alignItems:'center', gap:8}}>
                                {dpCardIcon && <i className={'pi ' + dpCardIcon}/>} {dpCardTitle}
                            </span>
                            {dpCardNote && <small style={{color:'#6b7280'}}>{dpCardNote}</small>}
                          </div>}
                  </div>}
              </div>}
            {/* Price protection floor, always in the hero: above the suggestion block
                when there is one, above the Dynamic Pricing button when there is not,
                so the limit is readable without opening the tab. */}
            <div className="pux-hero-minlimit">
                <span className="pux-hero-minlimit-lab">
                    <i className="pi pi-shield" style={{marginRight:'4px'}}/>{t('reprice.min_value')}
                </span>
                <span className={"pux-hero-minlimit-val" + (minLimit ? "" : " muted")}>
                    {minLimit ? formatCurrency(minLimit, stats.currency) : t('product.not_set')}
                </span>
            </div>
            {hasSuggestion && <div className="pux-hero-sugg">
                {target != null && <div className="pux-hero-sugg-row">
                    <span className="pux-hero-sugg-lab">{t('product_detail.hero_target_price', 'Suggested target price')}</span>
                    <span className="pux-hero-sugg-val muted">{formatCurrency(target, stats.currency)}</span>
                </div>}
                <div className="pux-hero-sugg-row">
                    <span className="pux-hero-sugg-lab">{t('product_detail.hero_applied_price', 'Price to apply')}</span>
                    <span className="pux-hero-sugg-val">
                        {formatCurrency(sugg, stats.currency)}
                        <span className={"pux-chip " + (up ? 'pux-chip-green' : 'pux-chip-amber')} style={{marginLeft:'6px'}}>
                            {up ? '↑ +' : '↓ −'}{formatCurrency(Math.abs(sugg - stats.myPrice), stats.currency)}
                        </span>
                    </span>
                </div>
            </div>}
            {/* Nothing configured (no template / product / campaign strategy) -> warn
                right in the hero, above the Dynamic Pricing button. */}
            {!chipLabel && <div style={{display:'flex', justifyContent:'flex-end', marginBottom:6}}>
                <span className="pux-chip pux-chip-amber" style={{cursor:'pointer'}}
                      onClick={() => onPrimaryAction('pricing')} title={t('reprice.no_dynamic_pricing_yet')}>
                    <i className="pi pi-exclamation-triangle"/> {t('reprice.no_dynamic_pricing_chip')}
                </span>
            </div>}
            <div style={{display:'flex', gap:8, justifyContent:'flex-end', flexWrap:'wrap', alignItems:'center'}}>
                <Button className="p-button-sm" icon="pi pi-compass"
                        label={t('product_detail.repricing')} onClick={() => onPrimaryAction('pricing')}/>
            </div>
        </div>
    </div>;
};
export default DecisionHero;
